import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { animate, query, stagger, style, transition, trigger } from "@angular/animations";
import { BonusSystemService } from "./bonusSystem.service";
import { CoreLogService } from "../../services/log.service";
import { Location } from "@angular/common";
import { LikeService } from "../../services/like.service";
import { ActivatedRoute, Router } from "@angular/router";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { RouterHelperService } from "../../services/routerHelper.service";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { Subscription } from "rxjs";
import { BonusactionBenefitViewModel, BonusActionViewModel, BonusLocationViewModel, NavigationTitleViewModel } from "../../shared/models";
import { Paging } from "../../shared/paging";
import { PagingHelper } from "../../shared/pagingHelper";
import { SortOption } from "../../shared/models/sort.model";
import { environment } from "../../../../environments/environment";
import { SortDirection, ContentType, SnackBarMessageType, BenefitRedemptionStatus } from "../../shared/contentTypes";
import { ScrollHelper } from "../../shared/scrollHelper";
import Utils from "../../shared/utils";
import { UserHelperService } from "../../services/userHelper.service";
import { WebvoucherDetailService } from "../webvoucherDetail/webvoucherDetail.service";
import { UserWebvoucherViewModel } from "../../shared/models/userInfo.model";
import { SafeHtml, DomSanitizer } from "@angular/platform-browser";
import { QRCode } from "../../forked/qr_code";
import { CoreLanguageService } from "../../services/language.service";
import { WebVoucherNumberLength } from "../../shared/models/web_voucher.model";
import { LoginDialogComponent } from "../../modalDialogs/logindialog/logindialog.component";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { WebvoucherDialogComponent } from "../../modalDialogs/webvoucherDialog/webvoucherDialog.component";
import { BonusWebvocherDialogComponent } from "../../modalDialogs/bonusWebvocherDialog/bonusWebvocherDialog.component";
import { FilterLocationDialogComponent } from "../../modalDialogs/filterLocationDialog/filterLocationDialog.component";
import { RedeemBenefitDialogComponent } from "../../modalDialogs";
import { WebShopService } from "../../services/webShop.service";
import { IBenefitCacheEntry } from "../../services/IWebShopService";
import { BenefitRedemptionService } from "../../services/benefitRedemption.service";

export enum BenefitState {
  Collect = 0,
  Redeem,
  RedeemAgain,
  CollectForNext
}

@Component({
  selector: "bonussystem",
  templateUrl: "./bonusSystem.component.html",
  styleUrls: ["./bonusSystem.component.scss"],
  providers: [BonusSystemService, WebvoucherDetailService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("bonusButtonEntering", [
      transition("false => true", [
        query(".animation", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})
export class BonusSystemComponent implements OnInit, OnDestroy {

  private static createLocationParticipationFilter(locationFilterIds: number[]): ((item: BonusActionViewModel) => boolean) {
    return (bonusAction: BonusActionViewModel): boolean => {
      if (bonusAction.locationsParticipated) {
        return (bonusAction.targetLocationIds.some(l => locationFilterIds.includes(l)));
      } else {
        return !(locationFilterIds.every(l => bonusAction.targetLocationIds.includes(l)));
      }
    };
  }

  private activeInterval: boolean = true; // to start Interval after loading page
  public isRegistered = false;
  public hasData: boolean = true; // check if there is data for this page;
  public wevoucherqrcodesvg: SafeHtml; // used to show and hide barcode at the top of the page
  public userWebvoucherViewModel: UserWebvoucherViewModel = new UserWebvoucherViewModel();
  public pageTitle: NavigationTitleViewModel = new NavigationTitleViewModel(); // Used as a title at the header of the page
  public bonusActionViewModelList: BonusActionViewModel[] = [];
  public paging: Paging<BonusActionViewModel>;
  private pagingHelper: PagingHelper = new PagingHelper();
  private sortOption: SortOption[] = [];
  public isLoaded: boolean = false;
  public locationFilterIds: number[] = [];
  private filterFunction: ((item: BonusActionViewModel) => boolean)[] = [];
  public contentType = ContentType;
  public fullpercent: number = 100;
  private _hasLikeFilter: boolean;
  public benefitState = BenefitState;
  private _state: BenefitRedemptionStatus;
  private _product_id: number;
  private _bonusFilterLocationIds: number[] = [];

  private langSubscriber: Subscription;
  private infiniteScrollSubscriber: Subscription;
  private bonusChangesSubscriber: Subscription;

  constructor(private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _bonusSystemService: BonusSystemService
    , private _location: Location
    , private _likeService: LikeService
    , private _activatedRoute: ActivatedRoute
    , private _router: Router
    , private internationalisationService: InternationalisationService
    , private _sanitized: DomSanitizer
    , private _snackBarService: SnackBarService
    , private _userHelperService: UserHelperService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _webvoucherDetailService: WebvoucherDetailService
    , private _routerHelperService: RouterHelperService
    , private _scrollHelper: ScrollHelper
    , private _bottomSheetHelperService: BottomSheetHelperService
    , private _webShopService: WebShopService
    , private _benefitRedemptionService: BenefitRedemptionService) { }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("BonusSystemComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.setRouteData();
    me.isRegistered = me._userHelperService.IsRegistered;
    if (me._userHelperService.IsRegistered) {
      me.getWebvoucherViewModel();
    } else {
      // webvoucher won't be shown at the top of the page.
    }

    me.langSubscriber = me.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    // set subscriber for scrollbar and load data for next page when it is in the exact point.
    me.infiniteScrollSubscriber = me._scrollHelper.InfiniteScrollObserver().subscribe((loadNextPage) => {
      if (loadNextPage && !Utils.IsNull(me.paging)) {
        me.bonusActionViewModelList.push(...me.paging.nextPage());
        me.PushChanges();
      }
    });

    // reload event data if data has been changed in storage.
    me.bonusChangesSubscriber = me._bonusSystemService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    //
    me.sortOption.push(me.pagingHelper.createSortOption("startDateYMD", SortDirection.DESC));

    // check if there is any location that has like. then just load the data for liked Locations
    // if there is no liked Location,  data for all Locations will be loaded.
    me._likeService.GetContentIDForActiveLikes(ContentType.Location)
      .then((locationIDs) => {
        if (locationIDs && locationIDs.length > 0) {
          me.locationFilterIds = locationIDs;
          me.filterFunction = [];
          me.filterFunction.push(BonusSystemComponent.createLocationParticipationFilter(me.locationFilterIds));
        }
        me.ReloadDataAndRender();
      });

  }

  /**
 * Set the title of the page by title Data in route data
 * This data has been set in RouterHelperService by each navigation Enh Evnt
 */
  private setRouteData() {
    const me = this;
    me.pageTitle = me._activatedRoute.snapshot.data["pageTitle"];
    me._hasLikeFilter = me._activatedRoute.snapshot.data["hasLikeFilter"];
  }

  /**
 * set Interval to chack bonus point changes
 */
  private refreshBonusAction() {
    const me = this;
    if (me.activeInterval) {
      me._routerHelperService.AddInterval(setInterval(function () {
        me._bonusSystemService.BonusActionInterval(me.bonusActionViewModelList)
          .then(() => {
            // bonusActionViewModelList has been updated
            me.PushChanges();
          })
          .catch(e => {
            me._coreLogService.error("BonusSystemComponent.refreshBonusAction", "BonusActionInterval", e);
          });
      }, environment.BonusActionInterval));
      me.activeInterval = false;
    }
  }

  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._bonusSystemService.GetBonusActionViewModel()
      .then(bonusActions => {
        if (bonusActions) {
          if (me._hasLikeFilter) {
            bonusActions = me._likeService.filterByLike(bonusActions);
          }
          const DefaultPageSize = 4;
          let CurrentPage = 2;
          if (!Utils.IsNull(me.paging)) {
            CurrentPage = me.paging.CurrentPage;
          }
          me.paging = new Paging(bonusActions, DefaultPageSize, me.sortOption, me.filterFunction);
          me.bonusActionViewModelList = []; // clean up
          me.bonusActionViewModelList.push(...me.paging.initLoad(CurrentPage));
          me.hasData = me.bonusActionViewModelList.length > 0;
          me.isLoaded = true;
          me.refreshBonusAction();
          me.checkBenefitInBasketAndSetValues();
          me.PushChanges();
        } else {
          me.hasData = false;
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch((e) => {
        me._coreLogService.error("BonusSystemComponent.ReloadDataAndRender", "GetBonusActionViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(3);
      });
  }

  /**get webvucher qrcode to show at top of the page */
  private getWebvoucherViewModel(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._webvoucherDetailService.GetWebvoucherQRCode()
      .then((response) => {
        if (response && response.webvoucherNumber) {
          me.userWebvoucherViewModel = response;
          const webvoucherNumber = response.webvoucherNumber.trim();
          if (webvoucherNumber.length == WebVoucherNumberLength) {
            me.wevoucherqrcodesvg = me._sanitized.bypassSecurityTrustHtml(new QRCode(webvoucherNumber)
              .svg());
          }
          me.PushChanges();
        } else {
          // No webvoucher found for user;
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch((e) => {
        me._coreLogService.error("BonusSystemComponent.getWebvoucherViewModel", "webvoucherDetailService.GetWebvoucherQRCode", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(4);
      });
  }

  /**
   * Open a ModalDialog for rating a event or watching event rating info
   */
  public openFilterDialog(): void {
    const me = this;
    const bottomSheetRef = me._bottomSheetHelperService.open(FilterLocationDialogComponent, {
      data: me.locationFilterIds,
    });
    // show snackBar after rating Item
    bottomSheetRef.afterDismissed().subscribe((result: number[]) => {
      if (result) {
        me.filterFunction = [];
        me.locationFilterIds = result;
        if (me.locationFilterIds && me.locationFilterIds.length > 0) {
          me.filterFunction.push(BonusSystemComponent.createLocationParticipationFilter(me.locationFilterIds));
        }
        me.paging.setFilter(me.filterFunction);
        me.paging.setSorting(me.sortOption);
        me.bonusActionViewModelList = []; // clean up
        me.bonusActionViewModelList.push(...me.paging.initLoad());
        me.hasData = me.bonusActionViewModelList.length > 0;
        me.PushChanges();
      }
    });
  }

  public async handleBenefitRedemption(benefit: BonusactionBenefitViewModel, bonusaction: BonusActionViewModel) {
    const me = this;
    const canRedeemBenefitOnline = await me._benefitRedemptionService.canRedeemBenefitOnline();
    if (canRedeemBenefitOnline) {
      me.selectRedeemBenefitOptions(benefit, bonusaction);
    } else {
      me.openQRCodeDialog();
    }
  }

  /**Description of the bonus action*/
  public hasDescription(description: string) {
    return !Utils.IsNullOrEmpty(description);
  }

  /** check if benefit product is in basket and set values*/
  public checkBenefitInBasketAndSetValues(): void {
    this._benefitRedemptionService.updateBenefitBasketStatusForList(this.bonusActionViewModelList);
  }

  /** Points that will be remain after the purchase*/
  public remainingPoints(currentPoints: number, pointsInBasket: number) {
    const points = currentPoints - pointsInBasket;
    return points;
  }

  /** Check whether a user can book a benefit more than once. 
   * @param currentPoints user points
   * @param pointsInBasket points in basket (benefit cost)
   * @param benefitCost benefit cost
   * @returns Boolean value, depending on the remaining points
   */
  private canRedeemAgain(currentPoints: number, pointsInBasket: number, benefitCost: number) {
    return this._benefitRedemptionService.canRedeemAgain(currentPoints, pointsInBasket, benefitCost);
  }

  /**Show hint if there are points in basket*/
  public showPointsInBasketLabel(pointsInBasket: number) {
    return this._benefitRedemptionService.showPointsInBasketLabel(pointsInBasket);
  }

  /** Hint about costs(points) for the benefit products in basket */
  public pointsInBasketLabel(currentPoints: number, benefitCostinBasket: number) {
    const me = this;
    return me._benefitRedemptionService.pointsInBasketLabel(currentPoints, benefitCostinBasket);
  }

  /**
   * @remainingPoints Points that will be remain after the purchase
   * @param benefitCost benefit cost (points)
   * @returns percentage
   */
  public benefitInBasketPercentage(benefitCost: number, currentPoints: number, pointsInBasket: number) {
    return this._benefitRedemptionService.benefitInBasketPercentage(benefitCost, currentPoints, pointsInBasket);
  }

  /**
   * Bestimmt den Status eines Benefits basierend auf Punktestand und Warenkorb
   * @param benefit Das Benefit-Objekt
   * @param bonusActionViewModel Das BonusAction-Objekt mit Punkteinformationen
   * @returns BenefitStatus enum
   */
  public getBenefitStatus(benefit: BonusactionBenefitViewModel, bonusActionViewModel: BonusActionViewModel): BenefitState {
    const me = this;
    if (!benefit.isInBasket) {
      return benefit.benefitpercantage >= me.fullpercent
        ? BenefitState.Redeem
        : BenefitState.Collect;
    } else {
      // Benefit is IN basket - check if user has enough points for another redemption
      const canRedeemAgain = me.canRedeemAgain(
        bonusActionViewModel.points,
        bonusActionViewModel.pointsInBasket,
        benefit.cost
      );
      return canRedeemAgain
        ? BenefitState.RedeemAgain
        : BenefitState.CollectForNext;// Collect points for next redemption
    }
  }

  /**
   * redeem option depending on user points and required points
   * @param requiredPoints required points for benefit
   * @param userPoints user points for single Benefit
   * @param product_id  benefit product id
   * @param bonusLocations ID of stored locations
   */
  private selectRedeemBenefitOptions(benefit: BonusactionBenefitViewModel, bonusaction: BonusActionViewModel) {
    const me = this;
    if (me._userHelperService.IsRegistered) {
      const canRedeemPoints = bonusaction.points >= benefit.cost;
      if (canRedeemPoints) {
        const isValid = me._benefitRedemptionService.handleConsumedPointsValidation(benefit.cost, bonusaction.points, bonusaction.pointsInBasket);
        if (!isValid) { return; }
        const benefitData = me._benefitRedemptionService.createBenefitCacheEntry(benefit);
        for (const location of bonusaction.locations) {
          me._bonusFilterLocationIds.push(location.hyp_cus_nr);
        }
        me._product_id = benefit.product_id;
        me._openSelectRedeemOptionDialog(benefitData);
      } else {
        me.openQRCodeDialog();
      }
    } else {
      me._bottomSheetHelperService.open(LoginDialogComponent);
    }
  }

  /** redeem at place or online */
  private _openSelectRedeemOptionDialog(benefitData: IBenefitCacheEntry) {
    const me = this;
    const dialogRef = me._bottomSheetHelperService.open(RedeemBenefitDialogComponent, {
    });
    dialogRef.afterDismissed().subscribe(async (result) => {
      if (result === "store") {
        me.openQRCodeDialog();
      } else if (result === "online") {
        await me.startRedeemProcess(benefitData);
      } else {
        // do nothing
      }
    });
  }

  /**case location and order mode are selected
   * case location and order mode are not selected */
  public async startRedeemProcess(benefitData: IBenefitCacheEntry) {
    const me = this;
    await me._benefitRedemptionService.startRedeemProcess(me._product_id, me._bonusFilterLocationIds, benefitData);
    me.PushChanges();
  }

  public openQRCodeDialog() {
    const me = this;
    if (!me._userHelperService.IsRegistered) {
      me._bottomSheetHelperService.open(LoginDialogComponent);
      return;
    }
    if (!me.userWebvoucherViewModel.webvoucherQRCode) {
      // ohne hinterlegten Gutscheincode gibt es keinen QR-Code anzuzeigen;
      // statt eines leeren Dialogs zuerst den Code erstellen lassen
      me.navigateToCreateWebvoucher();
      return;
    }
    const webvoucherModal = me._bottomSheetHelperService.open(BonusWebvocherDialogComponent, {
      data: me.userWebvoucherViewModel,
      autoFocus: false
    });
    webvoucherModal.afterDismissed().subscribe((response: boolean) => {
      if (response) {
        me.OpenWebVoucherModal();
      } else {
        // the modal dialog has been closed; Do nothing.
      }
    });
  }

  /** Hinweis zum Erstellen des QR Codes; nur fuer angemeldete Nutzer ohne hinterlegten Code */
  public get showCreateQRCodeHint(): boolean {
    const me = this;
    return me.isRegistered && !me.userWebvoucherViewModel.webvoucherQRCode;
  }

  /** Wechsel auf die Seite, auf der der Nutzer seinen Gutscheincode erstellt */
  public navigateToCreateWebvoucher(): void {
    const me = this;
    me._routerHelperService.setRouteTargetDataByKey("returnUrl", me._router.url);
    me._router.navigateByUrl("/webvoucher");
  }

  public createBonusActionPointsText(points: number) {
    const me = this;
    return me._coreLanguageService.value("phBonusAction_Cost_Points").replace("<points>", points.toString());
  }

  private OpenWebVoucherModal() {
    const me = this;
    const webvoucherModal = me._bottomSheetHelperService.open(WebvoucherDialogComponent, {
      data: me.userWebvoucherViewModel.webvoucherNumber,
      autoFocus: false
    });
    webvoucherModal.afterDismissed().subscribe((promise: Promise<boolean>) => {
      if (promise) {
        promise
          .then((response) => {
            if (response) {
              me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
              me.userWebvoucherViewModel.webvoucherQRCode = "";
              me.PushChanges();
            } else {
              me._snackBarService.ShowErrorMessageFailed(5);
            }
          })
          .catch((e) => {
            me._coreLogService.error("WebvoucherDetailComponent.openWebVoucherModal", "webvoucherModal.afterClosed (catch)", e);
            me._snackBarService.ShowErrorMessageFailed(6);
          });
      } else {
        // the modal dialog has been closed; Do nothing.
      }
    });
  }

  /**
   * navigate to BonusAction Detail
   * @param bonusActionId Id of BonusAction that will use for loading data in BonusActionDetail Page
   */
  public navigateToBonusActionDetail(bonusActionId: number | string): void {
    const me = this;
    if (me._userHelperService.IsRegistered) {
      if (me.userWebvoucherViewModel.webvoucherQRCode) {
        me._router.navigate(["/bonusactiondetail", bonusActionId]);
      } else {
        me.navigateToCreateWebvoucher();
      }
    } else {
      me._bottomSheetHelperService.open(LoginDialogComponent);
    }
  }

  public navigateBack(): void {
    this._routerHelperService.navigateBackSimple();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me.langSubscriber) {
      me.langSubscriber.unsubscribe();
    }
    if (me.infiniteScrollSubscriber) {
      me.infiniteScrollSubscriber.unsubscribe();
    }
    if (me.bonusChangesSubscriber) {
      me.bonusChangesSubscriber.unsubscribe();
    }
  }

}
